import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import { useTranslation } from "react-i18next";
import { 
  BookOpen, Calculator, FileText, ExternalLink, ChevronDown, ChevronRight,
  Target, BarChart3, Cog, Shield, FlaskConical, Truck, AlertTriangle,
  Ruler, Cpu, Leaf, Award, Users, TrendingUp, Microscope, Settings,
  ClipboardCheck, Package, Zap, Brain, Search, Sparkles, BookMarked, Car,
  Send, X, MessageSquare, Loader2, FileQuestion, Globe, Bot
} from "lucide-react";

const KnowledgeCenterPage = () => {
  const { t } = useTranslation('modules');
  const navigate = useNavigate();
  const [expandedTopic, setExpandedTopic] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDomain, setSelectedDomain] = useState("all");
  
  // AI Chat state
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSessionId, setAiSessionId] = useState(null);
  const [aiContext, setAiContext] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [aiMessages]);

  const sendAiMessage = async (message, context) => {
    if (!message.trim()) return;
    
    const userMsg = { role: "user", text: message };
    setAiMessages((prev) => [...prev, userMsg]);
    setAiInput("");
    setAiLoading(true);

    try {
      const res = await axios.post(
        `${API}/ai/chat`,
        { message, session_id: aiSessionId, context: context || aiContext },
        { withCredentials: true }
      );

      // Handle async job (long prompts)
      if (res.data.job_id) {
        setAiSessionId(res.data.session_id);
        const jobId = res.data.job_id;
        let attempts = 0;
        while (attempts < 30) {
          await new Promise(r => setTimeout(r, 3000));
          attempts++;
          try {
            const jobRes = await axios.get(`${API}/ai/jobs/${jobId}`, { withCredentials: true });
            if (jobRes.data.status === "completed" && jobRes.data.response) {
              setAiMessages((prev) => [...prev, { role: "ai", text: jobRes.data.response }]);
              return;
            } else if (jobRes.data.status === "failed") {
              throw new Error(jobRes.data.error || "Generation failed");
            }
          } catch (e) {
            if (e.response?.status !== 404) throw e;
          }
        }
        throw new Error("Timeout");
      }

      setAiSessionId(res.data.session_id);
      setAiMessages((prev) => [...prev, { role: "ai", text: res.data.response }]);
    } catch (err) {
      setAiMessages((prev) => [
        ...prev,
        { role: "ai", text: "Sorry, I couldn't process that request. Please try again." },
      ]);
    } finally {
      setAiLoading(false);
    }
  };

  const openAiForTopic = (topicTitle, domain) => {
    setAiContext(domain);
    setAiPanelOpen(true);
    const prompt = `I'd like to learn about ${topicTitle}. Give me a concise overview with key standards and best practices.`;
    sendAiMessage(prompt, domain);
  };

  const domains = [
    { id: "all", label: "All Domains", icon: BookOpen },
    { id: "reg-2026", label: "2026 Updates", icon: Globe },
    { id: "quality", label: "Quality Engineering", icon: Target },
    { id: "manufacturing", label: "Manufacturing Engineering", icon: Cog },
    { id: "automotive", label: "Automotive Quality", icon: Car },
    { id: "regulatory", label: "Regulatory & Compliance", icon: Shield },
    { id: "ai-compliance", label: "AI & Emerging Tech", icon: Bot },
    { id: "metrology", label: "Metrology & Testing", icon: Ruler },
    { id: "improvement", label: "Continuous Improvement", icon: TrendingUp },
    { id: "risk", label: "Risk Management", icon: AlertTriangle },
    { id: "consulting", label: "Quality Consulting", icon: Sparkles },
    { id: "validation", label: "Validation & Testing", icon: FlaskConical },
    { id: "training", label: "Training Programs", icon: BookMarked },
    { id: "supplier-qm", label: "Supplier QM", icon: Truck },
  ];

  const knowledgeBase = [
    // QUALITY ENGINEERING
    {
      id: "spc",
      domain: "quality",
      title: "Statistical Process Control (SPC)",
      icon: BarChart3,
      color: "#36A9E1",
      abstract: "SPC uses statistical methods to monitor and control production processes in real-time. By analyzing control chart patterns, organizations can detect process shifts before defects occur, reducing scrap and rework while maintaining consistent quality output.",
      description: "Monitor and control processes using statistical methods",
      topics: [
        "Control Charts (X-bar, R, S, p, np, c, u)",
        "Process Capability (Cp, Cpk, Pp, Ppk)",
        "Measurement System Analysis (MSA)",
        "Gage R&R Studies",
        "Western Electric Rules",
        "Nelson Rules",
        "CUSUM & EWMA Charts"
      ],
      standards: ["ISO 7870", "ISO 22514", "AIAG SPC Manual"],
      tools: [
        { name: "Cp/Cpk Calculator", type: "calculator" },
        { name: "Control Limit Calculator", type: "calculator" },
        { name: "Gage R&R Template", type: "template" }
      ],
      resources: [
        { title: "AIAG SPC Reference Manual", url: "#", type: "standard" },
        { title: "Shewhart Control Charts Guide", url: "#", type: "guide" },
        { title: "Process Capability Analysis", url: "#", type: "tutorial" }
      ]
    },
    {
      id: "six-sigma",
      domain: "quality",
      title: "Six Sigma & DMAIC",
      icon: Target,
      color: "#38BDF8",
      abstract: "Six Sigma is a rigorous, data-driven methodology that aims to achieve near-perfect quality (3.4 DPMO). The DMAIC framework (Define, Measure, Analyze, Improve, Control) provides a structured roadmap for identifying root causes of variation and implementing sustainable process improvements.",
      description: "Data-driven methodology for eliminating defects",
      topics: [
        "DMAIC Methodology",
        "DFSS (Design for Six Sigma)",
        "Belt Certification Levels",
        "Voice of Customer (VOC)",
        "Critical to Quality (CTQ)",
        "SIPOC Diagrams",
        "Sigma Level Calculations"
      ],
      standards: ["ISO 13053", "ASQ Six Sigma Body of Knowledge"],
      tools: [
        { name: "DPMO Calculator", type: "calculator" },
        { name: "Sigma Level Converter", type: "calculator" },
        { name: "SIPOC Template", type: "template" }
      ],
      resources: [
        { title: "DMAIC Roadmap", url: "#", type: "guide" },
        { title: "Six Sigma Green Belt Handbook", url: "#", type: "book" },
        { title: "Statistical Tools for Six Sigma", url: "#", type: "tutorial" }
      ]
    },
    {
      id: "quality-tools",
      domain: "quality",
      title: "7 Quality Tools & 7 New Tools",
      icon: ClipboardCheck,
      color: "#4ADE80",
      abstract: "The 7 Basic Quality Tools (Pareto, Fishbone, Check Sheets, Histograms, Scatter Plots, Flowcharts, Control Charts) and 7 New Management/Planning Tools form the essential toolkit for quality problem-solving, from shop-floor data analysis to strategic planning.",
      description: "Fundamental problem-solving and planning tools",
      topics: [
        "Pareto Charts",
        "Cause & Effect (Ishikawa/Fishbone)",
        "Check Sheets",
        "Histograms",
        "Scatter Diagrams",
        "Flowcharts",
        "Control Charts",
        "Affinity Diagrams",
        "Relations Diagrams",
        "Tree Diagrams",
        "Matrix Diagrams",
        "Arrow Diagrams",
        "PDPC (Process Decision Program Chart)"
      ],
      standards: ["ISO 9001:2015 Section 10.2"],
      tools: [
        { name: "Pareto Chart Generator", type: "calculator" },
        { name: "Fishbone Template", type: "template" },
        { name: "5 Whys Analysis Template", type: "template" }
      ],
      resources: [
        { title: "Quality Tools Handbook", url: "#", type: "guide" },
        { title: "Root Cause Analysis Guide", url: "#", type: "tutorial" }
      ]
    },
    {
      id: "doe",
      domain: "quality",
      title: "Design of Experiments (DOE)",
      icon: FlaskConical,
      color: "#FBBF24",
      abstract: "DOE is a systematic methodology to determine cause-and-effect relationships between process inputs and outputs. By varying multiple factors simultaneously, organizations can optimize product designs and manufacturing processes with fewer trials, reducing development time and cost.",
      description: "Systematic method to determine factor-response relationships",
      topics: [
        "Full Factorial Designs",
        "Fractional Factorial Designs",
        "Taguchi Methods",
        "Response Surface Methodology (RSM)",
        "Central Composite Designs",
        "Box-Behnken Designs",
        "ANOVA Analysis",
        "Confounding & Resolution"
      ],
      standards: ["ISO 3534-3", "NIST/SEMATECH Handbook"],
      tools: [
        { name: "DOE Sample Size Calculator", type: "calculator" },
        { name: "Factorial Design Generator", type: "calculator" },
        { name: "Taguchi L9 Template", type: "template" }
      ],
      resources: [
        { title: "DOE Simplified", url: "#", type: "book" },
        { title: "Taguchi Methods Guide", url: "#", type: "guide" }
      ]
    },
    {
      id: "reliability",
      domain: "quality",
      title: "Reliability Engineering",
      icon: Cog,
      color: "#FB923C",
      abstract: "Reliability Engineering ensures products and systems perform their required functions under stated conditions for a specified period. This discipline covers failure analysis, life testing, MTBF/MTTR calculations, and reliability prediction methods critical to safety-critical industries.",
      description: "Ensuring product performance and longevity under operating conditions",
      topics: [
        "MTBF / MTTR Calculations",
        "Weibull Analysis & Life Data Analysis",
        "Reliability Block Diagrams (RBD)",
        "Failure Rate & Hazard Function",
        "Accelerated Life Testing (ALT/HALT)",
        "Reliability Centered Maintenance (RCM)",
        "Reliability Growth Testing (Duane/AMSAA)",
        "FRACAS (Failure Reporting & Corrective Action)",
        "MIL-HDBK-217 / SN 29500 Prediction Methods",
        "Derating Analysis",
        "Warranty Data Analysis"
      ],
      standards: ["IEC 61709", "MIL-HDBK-217F", "IEC 62308", "SAE JA1011 (RCM)"],
      tools: [
        { name: "MTBF Calculator", type: "calculator" },
        { name: "Weibull Analysis Template", type: "template" },
        { name: "FRACAS Log Template", type: "template" }
      ],
      resources: [
        { title: "Reliability Engineering Handbook", url: "#", type: "book" },
        { title: "Weibull Analysis Guide", url: "#", type: "guide" },
        { title: "MIL-HDBK-217 Reference", url: "#", type: "standard" }
      ]
    },
    
    // MANUFACTURING ENGINEERING
    {
      id: "lean",
      domain: "manufacturing",
      title: "Lean Manufacturing",
      icon: Zap,
      color: "#E8A838",
      abstract: "Lean Manufacturing is a systematic approach to waste elimination while delivering value to the customer. Originating from the Toyota Production System, it encompasses 8 forms of waste (TIMWOODS), value stream mapping, 5S, Kaizen, and JIT production methods.",
      description: "Systematic waste elimination and value stream optimization",
      topics: [
        "8 Wastes (TIMWOODS)",
        "Value Stream Mapping (VSM)",
        "5S Workplace Organization",
        "Kaizen Events",
        "Kanban Systems",
        "Just-in-Time (JIT)",
        "Poka-Yoke (Error Proofing)",
        "SMED (Quick Changeover)",
        "TPM (Total Productive Maintenance)",
        "Heijunka (Level Loading)",
        "Takt Time Calculations"
      ],
      standards: ["SAE J4000", "ISO 18404"],
      tools: [
        { name: "Takt Time Calculator", type: "calculator" },
        { name: "OEE Calculator", type: "calculator" },
        { name: "VSM Template", type: "template" },
        { name: "5S Audit Checklist", type: "template" }
      ],
      resources: [
        { title: "Toyota Production System Guide", url: "#", type: "book" },
        { title: "Value Stream Mapping Handbook", url: "#", type: "guide" },
        { title: "Lean Implementation Roadmap", url: "#", type: "tutorial" }
      ]
    },
    {
      id: "process-eng",
      domain: "manufacturing",
      title: "Process Engineering",
      icon: Settings,
      color: "#36A9E1",
      abstract: "Process Engineering covers the design, control, and optimization of manufacturing processes. It integrates FMEA, control plans, work instructions, and validation protocols (IQ/OQ/PQ) to ensure processes consistently produce conforming products.",
      description: "Design, control, and optimization of manufacturing processes",
      topics: [
        "Process Flow Diagrams (PFD)",
        "Process FMEA (PFMEA)",
        "Control Plans",
        "Work Instructions",
        "Cycle Time Analysis",
        "Line Balancing",
        "Bottleneck Analysis",
        "Process Validation (IQ/OQ/PQ)",
        "Equipment Qualification"
      ],
      standards: ["AIAG APQP", "AIAG PPAP", "ISO 9001 Section 8"],
      tools: [
        { name: "Cycle Time Calculator", type: "calculator" },
        { name: "Line Balance Calculator", type: "calculator" },
        { name: "PFMEA Template", type: "template" },
        { name: "Control Plan Template", type: "template" }
      ],
      resources: [
        { title: "APQP & PPAP Reference Manual", url: "#", type: "standard" },
        { title: "Process Validation Guide", url: "#", type: "guide" }
      ]
    },
    {
      id: "dfm",
      domain: "manufacturing",
      title: "Design for Manufacturing (DFM/DFA)",
      icon: Package,
      color: "#F87171",
      abstract: "DFM/DFA optimizes product designs to minimize manufacturing complexity and assembly time, reducing production costs while improving quality. This discipline integrates GD&T, tolerance analysis, and design reviews early in the product development lifecycle.",
      description: "Optimize product designs for manufacturability and assembly",
      topics: [
        "DFM Principles",
        "DFA (Design for Assembly)",
        "DFMEA (Design FMEA)",
        "Tolerance Analysis",
        "GD&T (Geometric Dimensioning)",
        "Design Reviews",
        "Prototype Validation",
        "Cost Estimation"
      ],
      standards: ["ASME Y14.5", "ISO 1101", "AIAG FMEA Manual"],
      tools: [
        { name: "Tolerance Stack Calculator", type: "calculator" },
        { name: "DFMEA Template", type: "template" },
        { name: "Design Review Checklist", type: "template" }
      ],
      resources: [
        { title: "GD&T Fundamentals", url: "#", type: "guide" },
        { title: "DFM Handbook", url: "#", type: "book" }
      ]
    },
    
    // AUTOMOTIVE QUALITY
    {
      id: "iatf",
      domain: "automotive",
      title: "IATF 16949 Quality Management System",
      icon: Car,
      color: "#F87171",
      abstract: "IATF 16949:2016 is the global automotive QMS standard, mandatory for all automotive supply chain participants. It builds on ISO 9001 with automotive-specific requirements for product safety, defect prevention, and reduction of variation and waste. All major OEMs require this certification.",
      description: "THE automotive QMS standard for design, development, production, and service of automotive products",
      topics: [
        "IATF 16949:2016 Full Requirements Overview",
        "Customer-Specific Requirements (CSR) — GM, Ford, Stellantis, VW, BMW, Toyota",
        "Context of the Organization (Clause 4) — Interested Parties & Scope",
        "Risk-Based Thinking (Clause 6.1) — Contingency Planning",
        "Product Safety (Section 4.4.1.2) — Product Safety Representative",
        "Warranty Management (Section 10.2.6) — NTF Analysis",
        "Supplier Development (Section 8.4) — Second-Party Audits",
        "Manufacturing Feasibility Assessment (Section 8.2.3.1.3)",
        "IATF Certification Scheme Rules (5th Edition)",
        "Sanctioned Interpretations (SIs) & FAQs",
        "Performance KPIs — PPM, Delivery, Scrap Rate"
      ],
      standards: ["IATF 16949:2016", "ISO 9001:2015", "OEM Customer-Specific Requirements"],
      tools: [
        { name: "IATF Gap Assessment", type: "template" },
        { name: "CSR Compliance Matrix", type: "template" },
        { name: "Certification Readiness Checklist", type: "template" }
      ],
      resources: [
        { title: "IATF 16949:2016 Standard", url: "#", type: "standard" },
        { title: "Sanctioned Interpretations", url: "#", type: "guide" },
        { title: "IATF Auditor Guide", url: "#", type: "guide" }
      ]
    },
    {
      id: "aiag-core-tools",
      domain: "automotive",
      title: "AIAG Core Tools (APQP/PPAP/FMEA/MSA/SPC)",
      icon: ClipboardCheck,
      color: "#36A9E1",
      abstract: "The 5 AIAG Core Tools are mandatory quality planning methods for the automotive supply chain. APQP structures product launch, PPAP documents production readiness, FMEA identifies potential failures, MSA validates measurement systems, and SPC monitors ongoing processes.",
      description: "Essential quality planning and control tools for automotive",
      topics: [
        "APQP 3rd Edition - 5 Phases",
        "PPAP 4th Edition - 18 Elements",
        "AIAG-VDA FMEA Handbook",
        "MSA 4th Edition",
        "SPC 2nd Edition",
        "Run@Rate Studies",
        "Safe Launch",
        "Production Trial Runs",
        "PSW (Part Submission Warrant)",
        "Control Plan Methodology"
      ],
      standards: ["AIAG APQP", "AIAG PPAP", "AIAG-VDA FMEA", "AIAG MSA", "AIAG SPC"],
      tools: [
        { name: "APQP Timing Chart", type: "template" },
        { name: "PPAP Checklist (18 Elements)", type: "template" },
        { name: "AIAG-VDA FMEA Template", type: "template" },
        { name: "MSA Study Template", type: "template" }
      ],
      resources: [
        { title: "AIAG Core Tools Manuals", url: "#", type: "standard" },
        { title: "PPAP Submission Guide", url: "#", type: "guide" },
        { title: "FMEA Handbook Training", url: "#", type: "tutorial" }
      ]
    },
    {
      id: "vda-standards",
      domain: "automotive",
      title: "VDA Standards (German Automotive)",
      icon: Award,
      color: "#FBBF24",
      abstract: "The VDA (Verband der Automobilindustrie) standards are quality management publications from the German automotive association, widely adopted by German OEMs (BMW, VW, Mercedes-Benz). Key publications include VDA 6.3 process audits and VDA 6.5 product audits.",
      description: "German Association of the Automotive Industry standards",
      topics: [
        "VDA 6.3 Process Audit",
        "VDA 6.5 Product Audit",
        "VDA 2 Production Process & Product Approval",
        "VDA Maturity Level Assurance",
        "VDA Automotive SPICE",
        "VDA Field Failure Analysis",
        "VDA Robust Production Process",
        "VDA Problem Solving (8D)",
        "German OEM Requirements (BMW, VW, Mercedes)"
      ],
      standards: ["VDA 6.3", "VDA 6.5", "VDA 2", "VDA MLA", "A-SPICE"],
      tools: [
        { name: "VDA 6.3 Audit Checklist", type: "template" },
        { name: "VDA Maturity Assessment", type: "template" },
        { name: "VDA 8D Report Template", type: "template" }
      ],
      resources: [
        { title: "VDA QMC Publications", url: "#", type: "standard" },
        { title: "VDA 6.3 Auditor Training", url: "#", type: "training" }
      ]
    },
    {
      id: "cqi-standards",
      domain: "automotive",
      title: "CQI Special Process Standards",
      icon: Settings,
      color: "#38BDF8",
      abstract: "CQI (Continuous Quality Improvement) standards are AIAG special process assessments for critical automotive manufacturing processes including heat treating, plating, coating, welding, soldering, molding, and casting. These assessments ensure process controls meet OEM requirements.",
      description: "AIAG special process assessments for automotive",
      topics: [
        "CQI-9 Heat Treat System Assessment",
        "CQI-11 Plating System Assessment",
        "CQI-12 Coating System Assessment",
        "CQI-15 Welding System Assessment",
        "CQI-17 Soldering System Assessment",
        "CQI-23 Molding System Assessment",
        "CQI-27 Casting System Assessment",
        "Job Audit Requirements",
        "Process Tables Compliance"
      ],
      standards: ["CQI-9", "CQI-11", "CQI-12", "CQI-15", "CQI-17", "CQI-23", "CQI-27"],
      tools: [
        { name: "CQI-9 Checklist", type: "template" },
        { name: "CQI-12 Audit Template", type: "template" },
        { name: "Special Process Gap Assessment", type: "template" }
      ],
      resources: [
        { title: "AIAG CQI Publications", url: "#", type: "standard" },
        { title: "Special Process Guide", url: "#", type: "guide" }
      ]
    },
    {
      id: "automotive-problem-solving",
      domain: "automotive",
      title: "Automotive Problem Solving (8D/QRQC)",
      icon: Brain,
      color: "#4ADE80",
      abstract: "Structured problem-solving methods such as Global 8D and QRQC are essential for the automotive industry. These methodologies ensure containment, root cause identification, and permanent corrective actions are implemented effectively to prevent recurrence.",
      description: "Structured problem solving methods for automotive",
      topics: [
        "8D Methodology (Ford/AIAG)",
        "Global 8D",
        "QRQC (Quick Response Quality Control)",
        "5 Why Analysis",
        "Is/Is Not Analysis",
        "Kepner-Tregoe",
        "Red Flag Conditions",
        "Containment Actions",
        "Permanent Corrective Actions",
        "Lessons Learned Deployment"
      ],
      standards: ["AIAG CQI-20", "Ford 8D", "VDA 8D"],
      tools: [
        { name: "8D Report Template", type: "template" },
        { name: "5 Why Worksheet", type: "template" },
        { name: "QRQC Board Template", type: "template" }
      ],
      resources: [
        { title: "CQI-20 Problem Solving Guide", url: "#", type: "standard" },
        { title: "8D Training Materials", url: "#", type: "training" }
      ]
    },
    
    // AI & EMERGING TECHNOLOGY COMPLIANCE (NEW - Issue #2)
    {
      id: "ai-qms",
      domain: "ai-compliance",
      title: "AI/ML Quality Management (ISO/IEC 42001)",
      icon: Bot,
      color: "#818CF8",
      abstract: "ISO/IEC 42001:2023 is the world's first international standard for AI Management Systems (AIMS). It provides a framework for organizations developing or using AI to manage risks, ensure responsible AI deployment, and demonstrate compliance with emerging regulatory requirements including the EU AI Act.",
      description: "International standard for Artificial Intelligence Management Systems",
      topics: [
        "ISO/IEC 42001:2023 — AI Management System Requirements",
        "AI Risk Assessment & Mitigation (ISO/IEC 23894)",
        "AI System Lifecycle Management",
        "Bias Detection & Fairness Metrics",
        "Explainability & Transparency Requirements",
        "AI Model Validation & Verification",
        "Data Quality for AI/ML (ISO/IEC 5259)",
        "AI Impact Assessment Methodology",
        "Responsible AI Governance Framework",
        "AI Audit Trail & Documentation Requirements",
        "Continuous Monitoring of AI System Performance"
      ],
      standards: ["ISO/IEC 42001:2023", "ISO/IEC 23894", "ISO/IEC 5259", "ISO/IEC 38507"],
      tools: [
        { name: "AI Risk Assessment Template", type: "template" },
        { name: "AI Impact Assessment Form", type: "template" },
        { name: "Model Validation Checklist", type: "template" },
        { name: "AI Bias Audit Template", type: "template" }
      ],
      resources: [
        { title: "ISO/IEC 42001:2023 Implementation Guide", url: "#", type: "standard" },
        { title: "NIST AI Risk Management Framework", url: "#", type: "guide" },
        { title: "White Paper: State of AI in Quality Management 2025", url: "#", type: "whitepaper" }
      ]
    },
    {
      id: "eu-ai-act",
      domain: "ai-compliance",
      title: "EU AI Act & Global AI Regulation",
      icon: Globe,
      color: "#F472B6",
      abstract: "The EU AI Act (Regulation 2024/1689) is the world's first comprehensive legal framework for AI, classifying AI systems by risk level (Unacceptable, High, Limited, Minimal). Organizations deploying AI in the EU must comply with specific obligations including conformity assessments, transparency, and human oversight.",
      description: "Comprehensive regulatory framework for AI systems worldwide",
      topics: [
        "EU AI Act Risk Classification (Unacceptable/High/Limited/Minimal)",
        "High-Risk AI System Requirements (Article 9-15)",
        "Conformity Assessment for High-Risk AI",
        "AI Transparency & Disclosure Obligations",
        "Prohibited AI Practices (Article 5)",
        "Human Oversight Requirements",
        "Technical Documentation for AI Systems",
        "Post-Market Monitoring of AI Systems",
        "US Executive Order on AI Safety (EO 14110)",
        "NIST AI Risk Management Framework (AI RMF 1.0)",
        "Canada AIDA (Artificial Intelligence and Data Act)",
        "Brazil AI Bill (PL 2338/2023)",
        "Penalties & Enforcement Mechanisms"
      ],
      standards: ["EU AI Act 2024/1689", "NIST AI RMF 1.0", "EO 14110", "UNESCO AI Ethics"],
      tools: [
        { name: "AI Risk Classification Questionnaire", type: "template" },
        { name: "EU AI Act Compliance Checklist", type: "template" },
        { name: "AI System Technical Documentation Template", type: "template" }
      ],
      resources: [
        { title: "EU AI Act Full Text & Guidance", url: "#", type: "regulation" },
        { title: "NIST AI RMF Playbook", url: "#", type: "guide" },
        { title: "White Paper: Global AI Regulatory Landscape 2025", url: "#", type: "whitepaper" },
        { title: "White Paper: AI Compliance Readiness for Quality Professionals", url: "#", type: "whitepaper" }
      ]
    },
    {
      id: "ai-medical-devices",
      domain: "ai-compliance",
      title: "AI/ML in Medical Devices (FDA SaMD)",
      icon: Cpu,
      color: "#67E8F9",
      abstract: "The FDA's framework for Software as a Medical Device (SaMD) with AI/ML addresses the unique challenge of continuously learning algorithms. This includes the Predetermined Change Control Plan, Good Machine Learning Practice (GMLP), and Total Product Lifecycle (TPLC) approach for AI-enabled medical devices.",
      description: "FDA and global regulatory frameworks for AI-powered medical devices",
      topics: [
        "FDA SaMD (Software as a Medical Device) Framework",
        "Predetermined Change Control Plan (PCCP)",
        "Good Machine Learning Practice (GMLP)",
        "Total Product Lifecycle (TPLC) for AI/ML",
        "FDA 510(k) for AI/ML Devices",
        "Real-World Performance Monitoring",
        "Clinical Evaluation of AI Algorithms",
        "EU MDR Requirements for AI Medical Devices",
        "Data Quality & Representativeness",
        "Algorithm Change Protocol (ACP)",
        "Locked vs Adaptive AI Algorithms"
      ],
      standards: ["FDA SaMD Guidance", "IEC 62304", "ISO 14971", "EU MDR 2017/745", "IMDRF SaMD Framework"],
      tools: [
        { name: "SaMD Classification Tool", type: "template" },
        { name: "PCCP Template", type: "template" },
        { name: "AI/ML Device Submission Checklist", type: "template" }
      ],
      resources: [
        { title: "FDA AI/ML Action Plan", url: "#", type: "regulation" },
        { title: "IMDRF SaMD Framework", url: "#", type: "standard" },
        { title: "White Paper: AI in Medical Devices — Regulatory Pathways 2025", url: "#", type: "whitepaper" }
      ]
    },
    {
      id: "ai-automotive",
      domain: "ai-compliance",
      title: "AI in Automotive (ADAS/AV Safety)",
      icon: Car,
      color: "#E8A838",
      abstract: "The integration of AI in automotive applications — from ADAS to autonomous vehicles — requires compliance with functional safety (ISO 26262), SOTIF (ISO 21448), and emerging cybersecurity standards (ISO/SAE 21434). This subsector covers the safety assurance framework for AI-driven automotive systems.",
      description: "Safety and quality standards for AI-powered automotive systems",
      topics: [
        "ISO 26262 Functional Safety for AI Systems",
        "ISO 21448 SOTIF (Safety of the Intended Functionality)",
        "ISO/SAE 21434 Automotive Cybersecurity",
        "UNECE WP.29 AV Regulations",
        "SAE J3016 Levels of Driving Automation",
        "Simulation & Virtual Validation of AI",
        "ODD (Operational Design Domain) Definition",
        "AI Model V&V for Safety-Critical Functions",
        "Data Recording & Event Data Recorders",
        "Type Approval for ADAS/AV"
      ],
      standards: ["ISO 26262", "ISO 21448", "ISO/SAE 21434", "UNECE R155/R156", "SAE J3016"],
      tools: [
        { name: "SOTIF Analysis Template", type: "template" },
        { name: "Automotive Cybersecurity Assessment", type: "template" },
        { name: "ODD Specification Template", type: "template" }
      ],
      resources: [
        { title: "ISO 21448 SOTIF Implementation Guide", url: "#", type: "guide" },
        { title: "White Paper: AI Safety Assurance in Autonomous Vehicles 2025", url: "#", type: "whitepaper" }
      ]
    },
    
    // REGULATORY & COMPLIANCE
    {
      id: "gmp",
      domain: "regulatory",
      title: "Good Manufacturing Practice (GMP)",
      icon: Shield,
      color: "#4ADE80",
      abstract: "GMP regulations ensure pharmaceutical and medical device products are consistently produced and controlled according to quality standards. Key frameworks include FDA 21 CFR 210/211 (pharma), 21 CFR 820 (devices), EU GMP, and ICH Q-series guidelines covering the full product lifecycle.",
      description: "Regulatory requirements for pharmaceutical and medical device manufacturing",
      topics: [
        "FDA 21 CFR Part 210/211 (Pharma)",
        "FDA 21 CFR Part 820 (Medical Devices)",
        "EU GMP Annex 1 (Sterile Manufacturing, Rev. 2023)",
        "ICH Q7 (API Manufacturing)",
        "ICH Q8/Q9/Q10 (Pharmaceutical Quality System)",
        "ICH Q12 (Lifecycle Management)",
        "ICH Q14 (Analytical Procedure Development)",
        "Data Integrity (ALCOA+)",
        "Validation Master Plans",
        "Change Control",
        "Deviation Management",
        "CAPA Systems"
      ],
      standards: ["21 CFR 210/211", "21 CFR 820", "EU GMP", "ICH Guidelines"],
      tools: [
        { name: "GMP Audit Checklist", type: "template" },
        { name: "Data Integrity Assessment", type: "template" },
        { name: "Change Control Form", type: "template" }
      ],
      resources: [
        { title: "FDA GMP Guide", url: "#", type: "regulation" },
        { title: "EU GMP Annex 1 (2023 Revision)", url: "#", type: "regulation" },
        { title: "ICH Q10 Implementation", url: "#", type: "guide" }
      ]
    },
    {
      id: "iso-standards",
      domain: "regulatory",
      title: "ISO Quality Standards",
      icon: Award,
      color: "#36A9E1",
      abstract: "ISO standards provide internationally recognized frameworks for quality management systems across all industries. ISO 9001 is the foundational QMS standard, with sector-specific extensions like ISO 13485 (medical), IATF 16949 (automotive), AS9100 (aerospace), and ISO 22000 (food safety).",
      description: "International standards for quality management systems",
      topics: [
        "ISO 9001:2015 (QMS — Expected revision 2025/2026)",
        "ISO 13485:2016 (Medical Devices QMS)",
        "ISO 14001:2015 (Environmental Management)",
        "ISO 45001:2018 (OH&S Management)",
        "ISO 17025:2017 (Laboratory Competence)",
        "ISO 22000:2018 (Food Safety)",
        "ISO 27001:2022 (Information Security)",
        "ISO 42001:2023 (AI Management Systems)",
        "IATF 16949:2016 (Automotive)",
        "AS9100 Rev D (Aerospace)",
        "Certification & Accreditation Process",
        "Integrated Management Systems (IMS)"
      ],
      standards: ["ISO 9001", "ISO 13485", "AS9100", "IATF 16949", "ISO 42001"],
      tools: [
        { name: "ISO 9001 Gap Assessment", type: "template" },
        { name: "Internal Audit Checklist", type: "template" },
        { name: "Management Review Template", type: "template" }
      ],
      resources: [
        { title: "ISO 9001:2015 Requirements Guide", url: "#", type: "standard" },
        { title: "Certification Roadmap", url: "#", type: "guide" },
        { title: "White Paper: ISO 9001 Revision Preview", url: "#", type: "whitepaper" }
      ]
    },
    {
      id: "fda-compliance",
      domain: "regulatory",
      title: "FDA & Global Regulatory Strategy",
      icon: FileText,
      color: "#FBBF24",
      abstract: "Regulatory strategy encompasses the selection of optimal submission pathways (510(k), PMA, De Novo, EUA), pre-submission engagement, and global market access planning. This includes FDA, EMA, ANVISA (Brazil), PMDA (Japan), NMPA (China), and Health Canada requirements for multi-market regulatory compliance.",
      description: "FDA submissions, global market access, and regulatory intelligence",
      topics: [
        "FDA 510(k) Substantial Equivalence Pathway",
        "FDA PMA (Premarket Approval) Process",
        "FDA De Novo Classification",
        "FDA Breakthrough Device Designation",
        "FDA Emergency Use Authorization (EUA)",
        "Pre-Submission (Q-Sub) Strategy",
        "FDA Inspection Readiness (BIMO, PAI)",
        "Warning Letter Response & Remediation",
        "EU MDR 2017/745 (Full Application May 2024+)",
        "EU IVDR 2017/746",
        "CE Marking & Notified Body Selection",
        "ANVISA (Brazil) — RDC 751/2022 Medical Devices",
        "PMDA (Japan) — JPAL & Shonin Submissions",
        "NMPA (China) — Class II/III Registration",
        "Health Canada — Medical Device License (MDL)",
        "UDI Requirements (FDA, EU, Global)",
        "Regulatory Intelligence & Change Tracking"
      ],
      standards: ["21 CFR", "EU MDR 2017/745", "EU IVDR 2017/746", "RDC ANVISA 751/2022"],
      tools: [
        { name: "Submission Pathway Decision Tree", type: "template" },
        { name: "510(k) Checklist", type: "template" },
        { name: "Inspection Readiness Checklist", type: "template" },
        { name: "Regulatory Submission Tracker", type: "template" }
      ],
      resources: [
        { title: "FDA Guidance Documents Database", url: "#", type: "regulation" },
        { title: "EU MDR Implementation Guide", url: "#", type: "guide" },
        { title: "White Paper: Global Medical Device Regulatory Landscape 2025", url: "#", type: "whitepaper" }
      ]
    },
    
    // METROLOGY & TESTING
    {
      id: "metrology",
      domain: "metrology",
      title: "Metrology & Calibration",
      icon: Ruler,
      color: "#38BDF8",
      abstract: "Metrology is the science of measurement, essential for ensuring measurement traceability, accuracy, and reliability. This domain covers calibration programs, measurement uncertainty (GUM), CMM programming, and maintaining NIST/national lab traceability chains.",
      description: "Measurement science, calibration, and traceability",
      topics: [
        "SI Units & Traceability",
        "Calibration Programs",
        "Measurement Uncertainty (GUM)",
        "Calibration Intervals",
        "Out-of-Tolerance Procedures",
        "CMM Programming",
        "Dimensional Inspection",
        "Surface Finish Measurement",
        "Optical Measurement",
        "NIST Traceability"
      ],
      standards: ["ISO 10012", "ISO 17025", "ANSI/NCSL Z540.3", "GUM"],
      tools: [
        { name: "Uncertainty Calculator", type: "calculator" },
        { name: "Calibration Interval Calculator", type: "calculator" },
        { name: "Calibration Certificate Template", type: "template" }
      ],
      resources: [
        { title: "GUM (Guide to Uncertainty)", url: "#", type: "standard" },
        { title: "Calibration Best Practices", url: "#", type: "guide" }
      ]
    },
    {
      id: "testing",
      domain: "metrology",
      title: "Testing & Inspection",
      icon: Microscope,
      color: "#F87171",
      abstract: "Testing and inspection methods verify that products meet specifications at every stage — incoming, in-process, and final release. This includes statistical sampling plans (AQL per ANSI Z1.4), NDT methods, environmental and reliability testing, and first article inspection (FAI).",
      description: "Product testing, inspection methods, and acceptance criteria",
      topics: [
        "Incoming Inspection",
        "In-Process Inspection",
        "Final Inspection",
        "AQL Sampling (ANSI Z1.4)",
        "Destructive Testing",
        "Non-Destructive Testing (NDT)",
        "Environmental Testing",
        "Reliability Testing",
        "Accelerated Life Testing",
        "First Article Inspection (FAI)"
      ],
      standards: ["ANSI/ASQ Z1.4", "MIL-STD-1916", "AS9102"],
      tools: [
        { name: "AQL Sample Size Calculator", type: "calculator" },
        { name: "Inspection Report Template", type: "template" },
        { name: "FAI Report Template", type: "template" }
      ],
      resources: [
        { title: "Sampling Procedures Guide", url: "#", type: "standard" },
        { title: "NDT Methods Handbook", url: "#", type: "guide" }
      ]
    },
    
    // CONTINUOUS IMPROVEMENT
    {
      id: "kaizen",
      domain: "improvement",
      title: "Kaizen & Continuous Improvement",
      icon: TrendingUp,
      color: "#4ADE80",
      abstract: "Kaizen is the philosophy of continuous incremental improvement, engaging all employees from the C-suite to the shop floor. Combined with structured methods like A3 thinking, PDCA, and Gemba walks, it creates a culture where problems are identified and solved systematically.",
      description: "Continuous incremental improvement methodologies",
      topics: [
        "Kaizen Events/Blitz",
        "Gemba Walks",
        "A3 Problem Solving",
        "PDCA/PDSA Cycle",
        "8D Problem Solving",
        "Suggestion Systems",
        "Quality Circles",
        "Benchmarking",
        "Best Practice Sharing",
        "Lessons Learned"
      ],
      standards: ["ISO 9001 Section 10", "Shingo Model"],
      tools: [
        { name: "A3 Report Template", type: "template" },
        { name: "8D Report Template", type: "template" },
        { name: "Kaizen Event Planner", type: "template" }
      ],
      resources: [
        { title: "Toyota Kata Handbook", url: "#", type: "book" },
        { title: "A3 Thinking Guide", url: "#", type: "guide" }
      ]
    },
    {
      id: "tqm",
      domain: "improvement",
      title: "Total Quality Management (TQM)",
      icon: Users,
      color: "#36A9E1",
      abstract: "TQM is an organization-wide approach to quality excellence that integrates management, employees, and supply partners in continuously improving processes, products, and services. Key frameworks include the Baldrige Excellence Model, EFQM, and Hoshin Kanri for strategic deployment.",
      description: "Organization-wide approach to quality excellence",
      topics: [
        "Deming's 14 Points",
        "Juran's Trilogy",
        "Crosby's Quality Principles",
        "Malcolm Baldrige Criteria",
        "EFQM Excellence Model",
        "Hoshin Kanri (Policy Deployment)",
        "Quality Function Deployment (QFD)",
        "Voice of Customer",
        "Customer Satisfaction Metrics",
        "Cost of Quality (COQ)"
      ],
      standards: ["Baldrige Framework", "EFQM Model", "ISO 9004"],
      tools: [
        { name: "Cost of Quality Calculator", type: "calculator" },
        { name: "QFD House of Quality", type: "template" },
        { name: "Baldrige Self-Assessment", type: "template" }
      ],
      resources: [
        { title: "Baldrige Excellence Framework", url: "#", type: "standard" },
        { title: "QFD Handbook", url: "#", type: "guide" }
      ]
    },
    
    // RISK MANAGEMENT
    {
      id: "fmea",
      domain: "risk",
      title: "FMEA (Failure Mode Effects Analysis)",
      icon: AlertTriangle,
      color: "#F87171",
      abstract: "FMEA is a proactive risk analysis method used to identify potential failure modes, assess their severity, occurrence, and detectability, and prioritize corrective actions. The AIAG-VDA FMEA handbook introduced the Action Priority (AP) system replacing traditional RPN.",
      description: "Systematic identification and prevention of potential failures",
      topics: [
        "Design FMEA (DFMEA)",
        "Process FMEA (PFMEA)",
        "System FMEA",
        "FMEA-MSR (Monitoring & System Response)",
        "Severity/Occurrence/Detection Ratings",
        "RPN vs AP (Action Priority)",
        "AIAG-VDA FMEA Handbook",
        "Reverse FMEA",
        "FMECA"
      ],
      standards: ["AIAG-VDA FMEA", "SAE J1739", "IEC 60812"],
      tools: [
        { name: "FMEA Template (AIAG-VDA)", type: "template" },
        { name: "RPN Calculator", type: "calculator" },
        { name: "Action Priority Matrix", type: "template" }
      ],
      resources: [
        { title: "AIAG-VDA FMEA Handbook", url: "#", type: "standard" },
        { title: "FMEA Best Practices", url: "#", type: "guide" }
      ]
    },
    {
      id: "risk-analysis",
      domain: "risk",
      title: "Risk Analysis & Management",
      icon: Brain,
      color: "#FBBF24",
      abstract: "Risk management encompasses the systematic identification, assessment, and mitigation of risks across the organization. ISO 31000 provides the overarching framework, while sector-specific standards like ISO 14971 (medical) and IEC 31010 provide implementation methods.",
      description: "Comprehensive risk identification, assessment, and mitigation",
      topics: [
        "ISO 14971 (Medical Device Risk)",
        "ISO 31000 (Risk Management)",
        "Hazard Analysis (HACCP, HAZOP)",
        "Fault Tree Analysis (FTA)",
        "Event Tree Analysis",
        "Bow-Tie Analysis",
        "Risk Matrices",
        "Risk Registers",
        "Risk-Based Thinking (ISO 9001)",
        "Supply Chain Risk Management"
      ],
      standards: ["ISO 14971", "ISO 31000", "IEC 31010"],
      tools: [
        { name: "Risk Matrix Template", type: "template" },
        { name: "Risk Register Template", type: "template" },
        { name: "FTA Diagram Template", type: "template" }
      ],
      resources: [
        { title: "ISO 14971 Implementation", url: "#", type: "guide" },
        { title: "Risk Assessment Methods", url: "#", type: "tutorial" }
      ]
    },
    {
      id: "supplier-quality",
      domain: "risk",
      title: "Supplier Quality Management",
      icon: Truck,
      color: "#38BDF8",
      abstract: "Supplier quality management ensures that externally provided products and services consistently meet requirements. This includes supplier selection, qualification audits, scorecards, quality agreements, and ongoing monitoring to maintain supply chain integrity.",
      description: "Supplier qualification, monitoring, and development",
      topics: [
        "Supplier Selection Criteria",
        "Supplier Audits",
        "Supplier Scorecards",
        "Approved Supplier List (ASL)",
        "Supplier CAPA",
        "Quality Agreements",
        "Supplier Development",
        "Second-Party Audits",
        "Incoming Inspection",
        "Skip-Lot Qualification"
      ],
      standards: ["ISO 9001 Section 8.4", "IATF 16949 Section 8.4"],
      tools: [
        { name: "Supplier Audit Checklist", type: "template" },
        { name: "Supplier Scorecard Template", type: "template" },
        { name: "Quality Agreement Template", type: "template" }
      ],
      resources: [
        { title: "Supplier Quality Handbook", url: "#", type: "guide" },
        { title: "Audit Techniques Guide", url: "#", type: "tutorial" }
      ]
    },
    // QUALITY CONSULTING SERVICE AREA
    {
      id: "qms-implementation",
      domain: "consulting",
      title: "QMS Implementation & Optimization",
      icon: Settings,
      color: "#36A9E1",
      abstract: "End-to-end QMS implementation covers gap analysis, documentation development, process mapping, training, internal audits, and certification readiness. Optimization involves streamlining existing systems to reduce overhead while maintaining compliance effectiveness.",
      description: "End-to-end QMS implementation and optimization for regulatory compliance",
      topics: [
        "QMS Gap Analysis Methodology",
        "Implementation Roadmap Planning",
        "Process Mapping & Documentation",
        "Management Review Setup",
        "KPI Development & Monitoring",
        "Certification Readiness Assessment",
        "Post-Certification Maintenance"
      ],
      standards: ["ISO 9001:2015", "ISO 13485", "IATF 16949", "AS9100 Rev D"],
      tools: [
        { name: "Gap Analysis Report Template", type: "template" },
        { name: "Implementation Plan Template", type: "template" },
        { name: "Management Review Template", type: "template" }
      ],
      resources: [
        { title: "QMS Implementation Guide", url: "#", type: "guide" },
        { title: "ISO 9001 Transition Roadmap", url: "#", type: "tutorial" }
      ]
    },
    {
      id: "design-controls",
      domain: "regulatory",
      title: "Design Controls (FDA 820.30 / ISO 13485)",
      icon: Settings,
      color: "#818CF8",
      abstract: "Design Controls are FDA-mandated procedures for medical device development, ensuring that design inputs are properly translated into validated outputs. The V-model framework links user needs through specifications, verification, and validation to design transfer.",
      description: "Structured design process required for medical devices and regulated products",
      topics: [
        "Design Planning & DHF Structure",
        "Design Input Requirements (User Needs to Specs)",
        "Design Output & Traceability Matrix",
        "Design Review (Concept, Preliminary, Critical, Final)",
        "Design Verification (meets specifications)",
        "Design Validation (meets user needs/intended use)",
        "Design Transfer to Manufacturing",
        "Design Changes & Impact Assessment",
        "Design History File (DHF) Management",
        "Risk-Based Design (ISO 14971 Integration)"
      ],
      standards: ["FDA 21 CFR 820.30", "ISO 13485:2016 Section 7.3", "EU MDR Annex II"],
      tools: [
        { name: "Design Input Requirements Template", type: "template" },
        { name: "Design Traceability Matrix", type: "template" },
        { name: "Design V&V Protocol", type: "template" }
      ],
      resources: [
        { title: "FDA Design Controls Guidance", url: "#", type: "regulation" },
        { title: "ISO 13485 Section 7.3 Guide", url: "#", type: "guide" }
      ]
    },
    {
      id: "change-management",
      domain: "consulting",
      title: "Change Control & Management",
      icon: Settings,
      color: "#FB7185",
      abstract: "Change Control ensures that all changes to products, processes, documents, and systems are properly evaluated, approved, and verified before implementation. This prevents uncontrolled changes from introducing nonconformities or regulatory non-compliance.",
      description: "Systematic management of changes to products, processes, documents, and systems",
      topics: [
        "Change Request Initiation & Classification",
        "Impact Assessment (Quality, Regulatory, Validation)",
        "Change Review Board (CRB) Process",
        "Regulatory Change Notification Requirements",
        "Post-Change Effectiveness Verification",
        "Emergency/Urgent Change Procedures",
        "Supplier Change Notifications (SCN)",
        "Customer Notification Requirements (IATF, FDA)",
        "Change Control for Validated Systems",
        "Document Change Control"
      ],
      standards: ["ISO 9001 Section 8.5.6", "FDA 21 CFR 820.30(i)", "IATF 16949 Section 8.5.6", "ICH Q10"],
      tools: [
        { name: "Change Control Request Form", type: "template" },
        { name: "Change Effectiveness Review", type: "template" }
      ],
      resources: [
        { title: "Change Management Best Practices", url: "#", type: "guide" },
        { title: "FDA Design Change Guidance", url: "#", type: "regulation" }
      ]
    },
    {
      id: "complaint-handling",
      domain: "regulatory",
      title: "Complaint Handling & Post-Market",
      icon: AlertTriangle,
      color: "#FDBA74",
      abstract: "Post-market complaint handling is a critical regulatory obligation for medical device and pharmaceutical companies. It encompasses complaint investigation, adverse event reporting (MDR/MedWatch), vigilance (EU), trend analysis, and PMCF to ensure ongoing product safety.",
      description: "Customer complaint management, adverse event reporting, and post-market surveillance",
      topics: [
        "Complaint Intake & Classification",
        "Investigation & Root Cause Analysis",
        "Health Hazard Evaluation",
        "MDR/MedWatch Reporting (FDA)",
        "Vigilance Reporting (EU MDR/IVDR)",
        "CAPA Linkage from Complaints",
        "Trend Analysis & Signal Detection",
        "Post-Market Surveillance (PMS) Plans",
        "Post-Market Clinical Follow-up (PMCF)",
        "Customer Communication & Resolution",
        "Complaint File Documentation (FDA 820.198)"
      ],
      standards: ["FDA 21 CFR 820.198", "FDA 21 CFR 803 (MDR)", "EU MDR Article 87-92", "ISO 13485 Section 8.2.2"],
      tools: [
        { name: "Complaint Investigation Form", type: "template" },
        { name: "Complaint Trend Report", type: "template" }
      ],
      resources: [
        { title: "FDA Complaint Handling Guidance", url: "#", type: "regulation" },
        { title: "Post-Market Surveillance Guide", url: "#", type: "guide" }
      ]
    },
    {
      id: "data-integrity",
      domain: "regulatory",
      title: "Data Integrity (ALCOA+ / Part 11 / Annex 11)",
      icon: Shield,
      color: "#67E8F9",
      abstract: "Data integrity is a critical FDA and EU GMP expectation. The ALCOA+ framework (Attributable, Legible, Contemporaneous, Original, Accurate, Complete, Consistent, Enduring, Available) defines the standard for trustworthy data in regulated environments.",
      description: "Data governance, electronic records integrity, and ALCOA+ compliance",
      topics: [
        "ALCOA+ Principles",
        "21 CFR Part 11 (Electronic Records/Signatures)",
        "EU GMP Annex 11 (Computerized Systems)",
        "Audit Trail Review & Management",
        "Data Governance Framework",
        "Blank Form Control",
        "Hybrid (Paper + Electronic) Systems",
        "Data Integrity Remediation Planning",
        "MHRA GxP Data Integrity Guidance",
        "WHO Data Integrity Guidance"
      ],
      standards: ["FDA 21 CFR Part 11", "EU GMP Annex 11", "MHRA GMP Data Integrity", "PIC/S PI 041-1"],
      tools: [
        { name: "ALCOA+ Assessment Template", type: "template" }
      ],
      resources: [
        { title: "FDA Data Integrity Guidance", url: "#", type: "regulation" },
        { title: "MHRA GMP Data Integrity Guide", url: "#", type: "regulation" }
      ]
    },
    {
      id: "csv-gamp",
      domain: "validation",
      title: "Computer System Validation (CSV / GAMP 5)",
      icon: Cpu,
      color: "#C084FC",
      abstract: "Computer System Validation ensures that GxP computerized systems are fit for intended use. GAMP 5 (2nd Edition) provides a risk-based approach to validating software from Category 3 (configurable) to Category 5 (custom), aligned with 21 CFR Part 11 and EU GMP Annex 11.",
      description: "Validation of computerized systems per GAMP 5, Part 11, and Annex 11",
      topics: [
        "GAMP 5 Software Categories (3/4/5)",
        "Risk-Based Validation Approach",
        "User Requirements Specification (URS)",
        "Functional Specification (FS)",
        "Configuration Specification (CS)",
        "IQ/OQ/PQ for Computerized Systems",
        "21 CFR Part 11 Compliance",
        "EU GMP Annex 11 Compliance",
        "Periodic Review & Revalidation",
        "Cloud/SaaS Qualification",
        "Data Migration Validation"
      ],
      standards: ["GAMP 5 2nd Edition", "FDA 21 CFR Part 11", "EU GMP Annex 11", "PIC/S PI 011-3"],
      tools: [
        { name: "CSV Validation Plan Template", type: "template" }
      ],
      resources: [
        { title: "GAMP 5 Guide (ISPE)", url: "#", type: "standard" },
        { title: "Part 11 Compliance Guide", url: "#", type: "regulation" }
      ]
    },
    {
      id: "validation-protocols",
      domain: "validation",
      title: "IQ/OQ/PQ Validation Protocols",
      icon: FlaskConical,
      color: "#A78BFA",
      abstract: "Equipment and process validation through IQ/OQ/PQ protocols ensures manufacturing processes are capable of consistently producing products meeting predetermined quality attributes. This lifecycle approach integrates FDA, EU Annex 15, and ICH Q8-Q12 requirements.",
      description: "IQ/OQ/PQ protocols, cleaning validation, and method validation services",
      topics: [
        "Installation Qualification (IQ)",
        "Operational Qualification (OQ)",
        "Performance Qualification (PQ)",
        "Cleaning Validation",
        "Method Validation (ICH Q2)",
        "Computer System Validation (CSV)",
        "Process Validation Lifecycle (FDA)",
        "Continued Process Verification"
      ],
      standards: ["FDA Process Validation Guidance", "EU Annex 15", "GAMP 5", "ICH Q2(R2)"],
      tools: [
        { name: "IQ Protocol Template", type: "template" },
        { name: "OQ Protocol Template", type: "template" },
        { name: "PQ Protocol Template", type: "template" },
        { name: "Cleaning Validation Protocol", type: "template" }
      ],
      resources: [
        { title: "FDA Process Validation Guidance (2011)", url: "#", type: "standard" },
        { title: "EU GMP Annex 15 Qualification Guide", url: "#", type: "guide" }
      ]
    },
    {
      id: "training-development",
      domain: "training",
      title: "GMP & Quality Training Programs",
      icon: BookMarked,
      color: "#34D399",
      abstract: "Effective training programs are a regulatory requirement and a critical success factor for quality systems. This covers GMP fundamentals, data integrity, GDP, internal auditor qualification, and competency-based training frameworks aligned with ICH Q10 and FDA requirements.",
      description: "GMP, data integrity, and sector-specific training for your teams",
      topics: [
        "GMP Fundamentals Training",
        "Data Integrity (ALCOA+ Principles)",
        "Good Documentation Practices (GDP)",
        "Deviation & CAPA Management Training",
        "Internal Auditor Qualification",
        "SPC & Statistical Methods",
        "Change Control Training",
        "Competency Assessment Methods"
      ],
      standards: ["ICH Q10", "FDA 21 CFR Part 211.25", "EU GMP Chapter 2"],
      tools: [
        { name: "Training Needs Assessment", type: "template" },
        { name: "Training Effectiveness Report", type: "template" }
      ],
      resources: [
        { title: "GMP Training Curriculum Guide", url: "#", type: "guide" },
        { title: "Competency-Based Training Framework", url: "#", type: "tutorial" }
      ]
    },
    {
      id: "regulatory-submissions",
      domain: "regulatory",
      title: "Regulatory Submission Strategy",
      icon: Shield,
      color: "#FB923C",
      abstract: "Regulatory submission strategy involves selecting the optimal market access pathway and preparing compliant submission packages. This includes FDA pre-submissions, ANVISA technical dossiers, EMA marketing authorization, and CE marking under EU MDR, tailored to the specific device classification and intended markets.",
      description: "FDA, ANVISA, EMA, Health Canada submission support and regulatory intelligence",
      topics: [
        "FDA Submission Pathways (510(k), PMA, De Novo)",
        "ANVISA Registration (RDC 751/2022 — Medical Devices)",
        "EMA Marketing Authorization (Centralized/Decentralized)",
        "CE Marking under EU MDR/IVDR",
        "Health Canada Medical Device License (MDL)",
        "TGA Australia Registration",
        "Regulatory Intelligence & Change Tracking",
        "Pre-Submission Meetings Strategy",
        "Post-Market Surveillance Requirements",
        "eCTD and Electronic Submission Formats"
      ],
      standards: ["FDA 21 CFR", "EU MDR 2017/745", "IVDR 2017/746", "RDC ANVISA 751/2022"],
      tools: [
        { name: "Submission Strategy Template", type: "template" },
        { name: "Regulatory Intelligence Report", type: "template" }
      ],
      resources: [
        { title: "FDA Device Classification Database", url: "#", type: "tool" },
        { title: "EU MDR Transition Timeline", url: "#", type: "guide" },
        { title: "White Paper: ANVISA Regulatory Updates 2025", url: "#", type: "whitepaper" }
      ]
    },
    {
      id: "supplier-qualification",
      domain: "supplier-qm",
      title: "Supplier Qualification & Monitoring",
      icon: Truck,
      color: "#F472B6",
      abstract: "Supplier qualification is a systematic process to evaluate, approve, and monitor suppliers based on quality, regulatory, and business criteria. This includes risk classification, on-site audits, quality agreements, performance scorecards, and change notification agreements.",
      description: "Supplier qualification, monitoring, and quality agreement management",
      topics: [
        "Supplier Risk Classification",
        "Qualification Questionnaire Design",
        "On-Site Supplier Audits",
        "Quality Agreement Development",
        "Supplier Performance Scorecards",
        "Approved Supplier List (ASL) Management",
        "Change Notification Agreements",
        "Second/Third Party Audit Management"
      ],
      standards: ["ISO 9001 Section 8.4", "FDA 21 CFR 820.50", "EU GMP Chapter 7"],
      tools: [
        { name: "Supplier Qualification Questionnaire", type: "template" },
        { name: "Quality Agreement Template", type: "template" },
        { name: "Supplier Performance Scorecard", type: "template" }
      ],
      resources: [
        { title: "Supplier Quality Management Handbook", url: "#", type: "guide" },
        { title: "Quality Agreement Best Practices", url: "#", type: "tutorial" }
      ]
    },
    // ============== 2026 REGULATORY UPDATES ==============
    {
      id: "samd-2026",
      domain: "reg-2026",
      title: "Software as a Medical Device (SaMD) — 2026 Landscape",
      icon: Cpu,
      color: "#818CF8",
      abstract: "In 2026, SaMD regulation shifts toward continuous lifecycle monitoring. The FDA QMSR (effective Feb 2, 2026) harmonizes with ISO 13485:2016, while new cybersecurity mandates require SBOM, penetration testing, and Algorithm Change Protocols (ACP) for AI/ML-based SaMD. IEC 62304 governs the software lifecycle, IEC 82304-1 covers standalone health software, and ISO 20417:2026 mandates unique software version identification.",
      description: "Complete regulatory framework for Software as a Medical Device in 2026",
      topics: [
        "IEC 62304 — Software Lifecycle Processes (Development, Maintenance, Risk)",
        "IEC 82304-1 — Health Software Products (Safety & Performance)",
        "ISO 14971:2019 — Software-Specific Risk Management",
        "FDA QMSR (Effective Feb 2, 2026) — ISO 13485 Harmonization",
        "Cybersecurity: SBOM (Software Bill of Materials) & VAPT Mandates",
        "AI/ML SaMD: Algorithm Change Protocol (ACP) Requirements",
        "ISO 20417:2026 — Labeling with Unique Software Version ID & eIFU",
        "FDA Section 524B — Cyber Device Requirements",
        "IEC 81001-5-1 — Health Software Cybersecurity Lifecycle",
        "ANVISA RDC 657/2022 — SaMD Cybersecurity & Interoperability (Brazil)",
        "EU MDR Rule 11 — SaMD Classification (Most Class IIa or Higher)",
        "EU AI Act (Aug 2026) — High-Risk AI System Obligations for SaMD",
        "Predetermined Change Control Plan (PCCP) for AI/ML Devices",
        "Real-World Performance Monitoring & Model Drift Detection"
      ],
      standards: ["IEC 62304", "IEC 82304-1", "IEC 81001-5-1", "ISO 14971:2019", "ISO 20417:2026", "FDA QMSR", "EU AI Act"],
      tools: [
        { name: "SaMD Classification Decision Tree", type: "template" },
        { name: "SBOM Structure Template (CycloneDX/SPDX)", type: "template" },
        { name: "Algorithm Change Protocol (ACP) Template", type: "template" },
        { name: "SaMD Cybersecurity Checklist", type: "template" }
      ],
      resources: [
        { title: "FDA SaMD Action Plan & QMSR Guide", url: "#", type: "regulation" },
        { title: "IEC 62304 2nd Edition Implementation", url: "#", type: "standard" },
        { title: "White Paper: SaMD Regulatory Convergence 2026", url: "#", type: "whitepaper" },
        { title: "White Paper: SBOM Best Practices for Medical Devices", url: "#", type: "whitepaper" }
      ]
    },
    {
      id: "api-gmp-2026",
      domain: "reg-2026",
      title: "Active Pharmaceutical Ingredients (API) — 2026 Digital GMP",
      icon: FlaskConical,
      color: "#34D399",
      abstract: "API manufacturing is moving toward 'Digital GMP' in 2026, replacing manual records with validated electronic systems. ICH Q7 remains the gold standard, while ICH Q9(R1) integrates quality risk management and ICH Q10 provides the pharmaceutical quality system framework. New 2026 requirements include EU veterinary API alignment (July 2026), stricter ALCOA+ audits, end-to-end supply chain traceability, and sustainability/green chemistry expectations.",
      description: "GMP compliance for Active Pharmaceutical Ingredients — 2026 updates",
      topics: [
        "ICH Q7 — Gold Standard GMP for API Manufacturing",
        "ICH Q9(R1) — Quality Risk Management Integration in API Processes",
        "ICH Q10 — Pharmaceutical Quality System (PQS) Lifecycle",
        "EU Regulation 2025/2154 — Veterinary API Standards Alignment (July 16, 2026)",
        "ALCOA+ Principles — 2026 Audit Focus for API Data Integrity",
        "Supply Chain Traceability — End-to-End Batch Traceability Requirements",
        "Mandatory Audits of Raw Material & Intermediate Suppliers",
        "Sustainability & Green Chemistry — Emission Controls & Safer Solvents",
        "Digital GMP — Validated Electronic Batch Records",
        "Process Validation Lifecycle for API (Stage 1/2/3)",
        "Cleaning Validation for Multi-Product API Facilities",
        "Cross-Contamination Prevention & Containment"
      ],
      standards: ["ICH Q7", "ICH Q9(R1)", "ICH Q10", "EU GMP Part II", "EU 2025/2154", "WHO TRS 1003"],
      tools: [
        { name: "API GMP Audit Checklist (2026)", type: "template" },
        { name: "ALCOA+ Data Integrity Assessment", type: "template" },
        { name: "Supply Chain Traceability Matrix", type: "template" },
        { name: "API Process Validation Protocol", type: "template" }
      ],
      resources: [
        { title: "ICH Q7 Implementation Guide", url: "#", type: "standard" },
        { title: "EU Regulation 2025/2154 Overview", url: "#", type: "regulation" },
        { title: "White Paper: Digital GMP for API Manufacturing 2026", url: "#", type: "whitepaper" }
      ]
    },
    {
      id: "fda-qmsr-2026",
      domain: "reg-2026",
      title: "FDA QMSR — The New Era (Effective Feb 2, 2026)",
      icon: Shield,
      color: "#F87171",
      abstract: "As of February 2, 2026, the FDA's Quality Management System Regulation (QMSR) officially replaces 21 CFR Part 820 (QSR) with a framework built on ISO 13485:2016. Key changes include the Medical Device File (MDF) replacing the DMR, Management Review and Quality Audits becoming fully inspectable, and the new Compliance Program 7382.850 replacing the legacy QSIT inspection technique.",
      description: "Complete guide to the FDA's QMSR transition from 21 CFR Part 820",
      topics: [
        "ISO 13485:2016 Incorporation by Reference",
        "DMR to Medical Device File (MDF) Transition",
        "Management Review Now Fully Inspectable (Removing FDA Exemptions)",
        "Quality Audits — Internal Audit Records Now Inspectable",
        "Compliance Program 7382.850 — Replaces Legacy QSIT",
        "Risk Management Alignment with ISO 14971:2019",
        "Premarket Pathways: Class I (Exempt), Class II (510(k)/De Novo), Class III (PMA)",
        "Cybersecurity for Cyber Devices — SBOM & Vulnerability Testing",
        "UDI (Unique Device Identification) on All Labels",
        "Facility Registration via FURLS (Annual)",
        "Quality Manual Cross-Reference: 21 CFR 820 to ISO 13485 Mapping",
        "FDA Does NOT Require ISO 13485 Certification (Alignment Only)"
      ],
      standards: ["FDA QMSR", "ISO 13485:2016", "ISO 14971:2019", "21 CFR Part 820 (Legacy)"],
      tools: [
        { name: "QMSR Transition Gap Analysis Template", type: "template" },
        { name: "21 CFR 820 to ISO 13485 Mapping Matrix", type: "template" },
        { name: "Medical Device File (MDF) Structure Template", type: "template" },
        { name: "FDA QMSR Inspection Readiness Checklist", type: "template" }
      ],
      resources: [
        { title: "FDA QMSR Final Rule (Feb 2026)", url: "#", type: "regulation" },
        { title: "ISO 13485:2016 Quick Reference", url: "#", type: "standard" },
        { title: "White Paper: Navigating the QMSR Transition", url: "#", type: "whitepaper" }
      ]
    },
    {
      id: "eudamed-mdr-2026",
      domain: "reg-2026",
      title: "EU MDR & EUDAMED — Mandatory May 2026",
      icon: Globe,
      color: "#38BDF8",
      abstract: "2026 marks the end of the voluntary period for EUDAMED. As of May 28, 2026, four key modules become mandatory: Actor Registration, UDI/Device Registration, Notified Bodies & Certificates, and Market Surveillance. New MDR devices must be registered before market placement; legacy devices by November 28, 2026. The Vigilance module follows in Q2 2027. Manufacturers must also proactively notify authorities of supply interruptions.",
      description: "EU MDR mandatory EUDAMED transition and regulatory requirements",
      topics: [
        "EUDAMED Mandatory Deadline: May 28, 2026 (4 Modules)",
        "Module 1: Actor Registration — Manufacturer, EC REP, Importer",
        "Module 2: UDI/Device Registration — Basic UDI-DI Required",
        "Module 3: Notified Bodies & Certificates",
        "Module 4: Market Surveillance",
        "New MDR Devices: Register Before Market Placement (May 28, 2026)",
        "Legacy Devices: Must Register by November 28, 2026",
        "Vigilance Module: Expected Mandatory Q2 2027",
        "Classification Rules (Annex VIII — 22 Rules)",
        "Clinical Evaluation (Annex XIV) — Continuous CER Required All Classes",
        "GSPR Checklist — General Safety & Performance Requirements",
        "Person Responsible for Regulatory Compliance (PRRC)",
        "European Authorized Representative (EC REP) — Mandatory for Non-EU",
        "Supply Interruption Notification (2025+ Requirement)"
      ],
      standards: ["EU MDR 2017/745", "EUDAMED", "MDCG Guidelines", "ISO 13485"],
      tools: [
        { name: "EUDAMED Registration Checklist", type: "template" },
        { name: "GSPR Compliance Matrix", type: "template" },
        { name: "Clinical Evaluation Plan (CEP) Template (Annex XIV)", type: "template" },
        { name: "MDR Classification Decision Tree", type: "template" }
      ],
      resources: [
        { title: "EU MDR 2017/745 Full Text", url: "#", type: "regulation" },
        { title: "EUDAMED Registration Guide", url: "#", type: "guide" },
        { title: "White Paper: EU MDR EUDAMED Readiness 2026", url: "#", type: "whitepaper" }
      ]
    },
    {
      id: "anvisa-2026",
      domain: "reg-2026",
      title: "ANVISA Brazil — Regulatory Reliance & 2026 Updates",
      icon: Shield,
      color: "#4ADE80",
      abstract: "ANVISA has introduced significant speed-to-market improvements through Normative Instruction No. 290/2024, allowing streamlined review for products approved by recognized foreign authorities (AREEs: FDA, Health Canada, TGA, MHLW). Class I/II devices use simplified electronic notification. Class III/IV require full dossier + B-GMP certification. All labels and IFU must be in Portuguese. Local BRH (Brazilian Registration Holder) is mandatory.",
      description: "Complete ANVISA compliance guide for medical devices in Brazil",
      topics: [
        "IN 290/2024 — Optimized Procedure for Foreign-Approved Devices",
        "Recognized Authorities (AREEs): FDA, TGA, Health Canada, MHLW",
        "Class I & II — Simplified Electronic Notification (No Expiry)",
        "Class III & IV — Full Technical Dossier + B-GMP Certification",
        "Brazilian Registration Holder (BRH) — Mandatory Local Representation",
        "INMETRO Certification — Mandatory for Electro-Medical (IEC 60601)",
        "ANATEL Certification — Required for Wireless Devices (Bluetooth/Wi-Fi)",
        "Labeling Requirements — All IFU in Portuguese",
        "RDC 657/2022 — SaMD Cybersecurity & Interoperability",
        "RDC 751/2022 — Medical Device Registration",
        "RDC 67/2009 — Technovigilance (Serious Adverse Events in 10 Days)",
        "RDC 551/2021 — Field Actions & Recall Notification",
        "UDI Implementation (Expected 2026)"
      ],
      standards: ["IN 290/2024", "RDC 751/2022", "RDC 657/2022", "RDC 67/2009", "RDC 551/2021"],
      tools: [
        { name: "ANVISA Registration Pathway Decision Tree", type: "template" },
        { name: "B-GMP Readiness Checklist", type: "template" },
        { name: "ANVISA Technovigilance Report Template", type: "template" }
      ],
      resources: [
        { title: "ANVISA IN 290/2024 Official Text", url: "#", type: "regulation" },
        { title: "ANVISA Regulatory Reliance Framework", url: "#", type: "guide" },
        { title: "White Paper: ANVISA Speed-to-Market Strategies 2026", url: "#", type: "whitepaper" }
      ]
    },
    {
      id: "cybersecurity-meddev-2026",
      domain: "reg-2026",
      title: "Medical Device Cybersecurity — 2026 Mandates",
      icon: Shield,
      color: "#F472B6",
      abstract: "Cybersecurity is now a mandatory QMS element. IEC 81001-5-1 leads secure development lifecycle requirements, while FDA Section 524B mandates SBOM and timely patches. The 48-hour vulnerability response is the new standard. Key tools include SonarQube/Veracode (SAST/DAST), Snyk/Black Duck (SBOM), Defensics (fuzz testing), and Burp Suite/OWASP ZAP (web interfaces). CISA KEV and CVSS scoring drive risk prioritization.",
      description: "Cybersecurity standards, SBOM requirements, and vulnerability management for medical devices",
      topics: [
        "IEC 81001-5-1 — Health Software Cybersecurity Lifecycle",
        "ISO/IEC 27001 — Organizational Information Security Baseline",
        "FDA Section 524B — SBOM & Timely Security Patches",
        "MDCG 2019-16 — EU IT Security Requirements",
        "ANVISA RDC 657/2022 — SaMD Data Protection & Integrity",
        "SBOM Structure: CycloneDX & SPDX Formats",
        "VEX (Vulnerability Exploitability Exchange) Files",
        "48-Hour Vulnerability Response Plan (SOP)",
        "Penetration Testing: SAST/DAST, Fuzz Testing, Network Testing",
        "CISA KEV (Known Exploited Vulnerabilities) Cross-Reference",
        "CVSS 4.0 Scoring — FDA Expects Mitigation for Score >= 7.0",
        "Cybersecurity Quality Agreement Clauses for Suppliers",
        "Coordinated Vulnerability Disclosure (CVD) Process",
        "MFA Enforcement & Software Update Integrity Verification"
      ],
      standards: ["IEC 81001-5-1", "ISO/IEC 27001", "FDA Section 524B", "MDCG 2019-16", "CISA KEV"],
      tools: [
        { name: "48-Hour Vulnerability Response SOP", type: "template" },
        { name: "Cybersecurity Quality Agreement Clause Template", type: "template" },
        { name: "SBOM Validation Checklist", type: "template" },
        { name: "Penetration Test Plan Template", type: "template" }
      ],
      resources: [
        { title: "IEC 81001-5-1 Implementation Guide", url: "#", type: "standard" },
        { title: "FDA Cybersecurity Guidance (2026)", url: "#", type: "regulation" },
        { title: "White Paper: Medical Device Cybersecurity Architecture 2026", url: "#", type: "whitepaper" },
        { title: "White Paper: SBOM & VEX for Regulated Software", url: "#", type: "whitepaper" }
      ]
    },
    {
      id: "pms-vigilance-2026",
      domain: "reg-2026",
      title: "Post-Market Surveillance & Vigilance — 2026 Deep Dive",
      icon: AlertTriangle,
      color: "#FBBF24",
      abstract: "Post-market surveillance in 2026 shifts from reactive complaint handling to proactive signal detection. Under FDA QMSR, feedback and regulatory agency feedback clauses are inspectable. EU MDR mandates PSUR (Class IIa+) and PMSR (Class I), with EUDAMED uploads starting May 2026. ANVISA requires 10-day serious adverse event reporting. Signal detection methodology (e.g., Proportional Reporting Ratio) is now a regulatory expectation.",
      description: "Comprehensive post-market surveillance, vigilance reporting, and signal detection",
      topics: [
        "FDA QMSR — Feedback (Clause 8.2.1) & Regulatory Feedback (8.2.6)",
        "FDA Section 522 — Post-Market Studies for Class II/III",
        "EU MDR — PSUR (Class IIa/b/III) vs PMSR (Class I)",
        "EUDAMED Market Surveillance Module (Mandatory May 2026)",
        "ANVISA Technovigilance (RDC 67/2009) — 10-Day Reporting",
        "ANVISA Field Actions (RDC 551/2021) — Recall Notification",
        "Signal Detection Methodology — Proportional Reporting Ratio",
        "PMCF (Post-Market Clinical Follow-up) — Active Data Collection",
        "Real-World Evidence (RWE) from Device Connectivity Logs",
        "CAPA Trigger Thresholds for Trend Data",
        "Field Safety Corrective Action (FSCA) — Universal Process",
        "Health Hazard Evaluation (HHE) — Risk Classification (Class I/II/III)",
        "Field Safety Notice (FSN) Drafting Best Practices"
      ],
      standards: ["EU MDR Articles 83-92", "FDA QMSR", "ANVISA RDC 67/2009", "MDCG 2020-7"],
      tools: [
        { name: "PMS Plan Template (EU MDR)", type: "template" },
        { name: "PSUR / PMSR Report Template", type: "template" },
        { name: "Signal Detection Dashboard Template", type: "template" },
        { name: "Field Action (Recall) Checklist", type: "template" }
      ],
      resources: [
        { title: "MDCG 2020-7 PMCF Guidance", url: "#", type: "standard" },
        { title: "FDA Post-Market Guidance", url: "#", type: "regulation" },
        { title: "White Paper: Proactive Signal Detection in MedTech 2026", url: "#", type: "whitepaper" }
      ]
    },
    {
      id: "auto-cyber-2026",
      domain: "reg-2026",
      title: "Automotive 2026 — ISO/SAE 21434 & IATF Updates",
      icon: Car,
      color: "#FB923C",
      abstract: "The automotive industry is shifting from 'Safety First' to 'Cyber-Safety First' as Software-Defined Vehicles become the norm. ISO/SAE 21434 mandates TARA (Threat Analysis and Risk Assessment) for all ECUs and CSMS for vehicle type approval (UN R155). IATF 16949 focuses on Sanctioned Interpretations for embedded software quality (Automotive SPICE) and ESG integration. The 6th Edition Rules redefine Extended Manufacturing Sites and shorten CAPA response times.",
      description: "Automotive cybersecurity engineering and IATF quality updates for 2026",
      topics: [
        "ISO/SAE 21434 — Cybersecurity Engineering for Road Vehicles",
        "TARA (Threat Analysis and Risk Assessment) — Mandatory for ECUs",
        "CSMS (Cybersecurity Management System) — UN R155 Type Approval",
        "IATF 16949:2016 — 2026 Focus on Software Quality & A-SPICE",
        "IATF 6th Edition Rules — Extended Manufacturing Sites, CAPA Timing",
        "Sanctioned Interpretations (SIs) for Embedded Software",
        "ESG Integration — Carbon Footprint & Conflict Minerals in IATF Audits",
        "Software-Defined Vehicles (SDV) Quality Requirements",
        "Automotive SPICE (A-SPICE) for Software Process Assessment",
        "Vehicle Type Approval for ADAS/AV Systems"
      ],
      standards: ["ISO/SAE 21434", "IATF 16949", "UN R155/R156", "A-SPICE", "IATF Rules 6th Ed."],
      tools: [
        { name: "TARA Worksheet Template", type: "template" },
        { name: "CSMS Gap Assessment Checklist", type: "template" },
        { name: "IATF 6th Edition Transition Checklist", type: "template" }
      ],
      resources: [
        { title: "ISO/SAE 21434 Implementation Guide", url: "#", type: "standard" },
        { title: "White Paper: Automotive Cybersecurity Type Approval 2026", url: "#", type: "whitepaper" }
      ]
    },
    {
      id: "aero-ia9100-2026",
      domain: "reg-2026",
      title: "Aerospace 2026 — IA9100 Transition (AS9100 Rebrand)",
      icon: Award,
      color: "#36A9E1",
      abstract: "The transition from AS9100 to IA9100 begins in 2026. Unlike previous versions, IA9100 mandates a formal Information Security Program (aligning with NIST 800-171 or CMMC), makes AS9145 APQP/PPAP mandatory (not just best practice), and introduces new 'Limited Scope' sub-tier audits focusing on how prime contractors manage quality and cybersecurity of their smallest suppliers. Phased rollout: Jan 2026 (early adopters) through late 2026 (full transition).",
      description: "Aerospace quality management system transition from AS9100 to IA9100",
      topics: [
        "IA9100 (2026) — AS9100 Rebrand with Enhanced Requirements",
        "Mandatory Information Security Program (NIST 800-171 / CMMC)",
        "AS9145 APQP & PPAP — Now Mandatory (Not Best Practice)",
        "Sub-Tier Oversight — New 'Limited Scope' Audits (Jan 2026)",
        "Phased Rollout: Jan 2026 (Early Adopters) → Late 2026 (Full)",
        "Configuration Management Enhancements",
        "FOD (Foreign Object Debris) Prevention Updates",
        "Counterfeit Parts Prevention (AS6174/AS5553)",
        "OASIS Database Integration",
        "First Article Inspection (FAI) per AS9102"
      ],
      standards: ["IA9100 (2026)", "AS9145", "AS9102", "NIST 800-171", "CMMC"],
      tools: [
        { name: "IA9100 Transition Gap Assessment", type: "template" },
        { name: "AS9145 APQP Checklist", type: "template" },
        { name: "InfoSec Program Readiness Template", type: "template" }
      ],
      resources: [
        { title: "IA9100 Transition Guide (IAQG)", url: "#", type: "standard" },
        { title: "NIST 800-171 Quick Start", url: "#", type: "guide" },
        { title: "White Paper: AS9100 to IA9100 Migration Roadmap", url: "#", type: "whitepaper" }
      ]
    },
    {
      id: "gap-analysis-2026",
      domain: "reg-2026",
      title: "2026 Global Regulatory Gap Analysis Framework",
      icon: Target,
      color: "#E8A838",
      abstract: "A structured gap analysis framework for the major 2026 regulatory transitions: FDA QMSR (21 CFR 820 → ISO 13485), EUDAMED mandatory registration, IA9100 rebrand, IATF 6th Edition Rules, AI Management Systems (ISO 42001), and SBOM automation. Includes a strategic implementation roadmap: Q1 (FDA QMSR), Q2 (EUDAMED), Q3 (supplier audits), Q4 (mock inspections). Also features a Supplier Risk Assessment (FMEA-style) with RPN scoring and a Cybersecurity Quality Agreement template.",
      description: "Master gap analysis for 2026 regulatory transitions with implementation roadmap",
      topics: [
        "QMS Gap: 21 CFR Part 820 (QSR) → FDA QMSR (ISO 13485:2016)",
        "Cyber Gap: Basic IT Security → SBOM & Vulnerability Management",
        "Aerospace Gap: AS9100 Rev D → IA9100 (APQP + InfoSec)",
        "Automotive Gap: IATF 16949:2016 → 6th Edition Rules",
        "Data Gap: Manual Tracking → EUDAMED (Mandatory May 2026)",
        "AI Gap: Unregulated AI Use → ISO 42001 (AIMS)",
        "Supplier Risk Assessment (FMEA-Style) — RPN Scoring",
        "Cybersecurity Quality Agreement Clause Template",
        "Strategic Roadmap: Q1 QMSR → Q2 EUDAMED → Q3 Suppliers → Q4 Mock Audit",
        "Supplier Onboarding: 5-Stage Gate Process (2026 Edition)",
        "48-Hour Vulnerability Disclosure SOP",
        "Mock Audit Scenario: 'The Connected Cardiac Patch'",
        "2026 Audit Readiness Scorecard (0-5 Scale)"
      ],
      standards: ["FDA QMSR", "EU MDR", "IA9100", "IATF 6th Ed.", "ISO 42001", "ISO/IEC 27001"],
      tools: [
        { name: "2026 Regulatory Gap Analysis Template", type: "template" },
        { name: "Supplier FMEA Risk Scorecard", type: "template" },
        { name: "Cybersecurity Quality Agreement Template", type: "template" },
        { name: "Mock Audit Scenario Pack", type: "template" },
        { name: "2026 Master Project Plan (Gantt)", type: "template" }
      ],
      resources: [
        { title: "2026 Compliance Toolkit Summary", url: "#", type: "guide" },
        { title: "White Paper: 2026 Regulatory Convergence Across Industries", url: "#", type: "whitepaper" },
        { title: "White Paper: Supplier Cybersecurity Governance 2026", url: "#", type: "whitepaper" }
      ]
    },
    {
      id: "aimd-2026",
      domain: "reg-2026",
      title: "Active Implantable Medical Devices (AIMD) — 2026 Checklist",
      icon: Microscope,
      color: "#C084FC",
      abstract: "AIMDs are high-risk Class III devices requiring the most stringent regulatory review. The ISO 14708 series covers general safety (Part 1) and device-specific requirements for cardiac pacemakers (Part 2), neurostimulators (Part 3), and circulatory support devices (Part 5). 2026 requirements include mandatory cybersecurity plans for networked implants, INMETRO/ANATEL certification in Brazil, and EU MDR implant cards for patients.",
      description: "Regulatory checklist for active implantable medical devices across USA, EU, and Brazil",
      topics: [
        "ISO 14708 Series — General Safety & Device-Specific Standards",
        "Part 2: Cardiac Pacemakers | Part 3: Neurostimulators | Part 5: VADs",
        "Biocompatibility (ISO 10993) — Local Effects After Implantation",
        "EMC & Wireless Safety — Essential Performance Under EMI",
        "Wireless Coexistence (Bluetooth/Wi-Fi vs Medical Functionality)",
        "FDA PMA — Most Stringent Premarket Review + Clinical Trial Data",
        "Post-Market Cybersecurity Management Plan (Networked AIMDs)",
        "INMETRO (Brazil) — Conformity Marks for Wireless AIMDs",
        "ANATEL (Brazil) — Certification for Wireless Modules",
        "EU MDR Implant Card — Patient Device Information & ID",
        "Long-Term Reliability & Performance Monitoring"
      ],
      standards: ["ISO 14708", "ISO 10993", "IEC 60601-1", "FDA PMA", "EU MDR 2017/745", "ANVISA"],
      tools: [
        { name: "AIMD Regulatory Pathway Checklist", type: "template" },
        { name: "Biocompatibility Evaluation Plan", type: "template" },
        { name: "EMC Test Plan Template", type: "template" }
      ],
      resources: [
        { title: "FDA PMA Guidance for Implantables", url: "#", type: "regulation" },
        { title: "ISO 14708 Series Overview", url: "#", type: "standard" },
        { title: "White Paper: Cybersecurity for Connected Implants 2026", url: "#", type: "whitepaper" }
      ]
    }
  ];

  const filteredKnowledge = knowledgeBase.filter(item => {
    const matchesDomain = selectedDomain === "all" || item.domain === selectedDomain;
    const matchesSearch = searchQuery === "" || 
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.topics.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesDomain && matchesSearch;
  });

  const toggleTopic = (id) => {
    setExpandedTopic(expandedTopic === id ? null : id);
  };

  return (
    <QMSLayout title={t('modules:knowledgeCenter.title')}>
      {/* Header */}
      <div className="mb-6">
        <h2 className="font-['Syne'] text-2xl font-bold text-white mb-2">
          {t('modules:knowledgeCenter.heading')}
        </h2>
        <p className="text-[#8B95A5]">
          {t('modules:knowledgeCenter.subtitle')}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="knowledge-stats">
        <div className="kpi-card" style={{ "--kpi-accent": "#36A9E1" }}>
          <p className="kpi-label">Topics</p>
          <p className="kpi-value">{knowledgeBase.length}</p>
        </div>
        <div className="kpi-card" style={{ "--kpi-accent": "#4ADE80" }}>
          <p className="kpi-label">Standards</p>
          <p className="kpi-value">{knowledgeBase.reduce((acc, k) => acc + k.standards.length, 0)}</p>
        </div>
        <div className="kpi-card" style={{ "--kpi-accent": "#38BDF8" }}>
          <p className="kpi-label">Tools</p>
          <p className="kpi-value">{knowledgeBase.reduce((acc, k) => acc + k.tools.length, 0)}</p>
        </div>
        <div className="kpi-card" style={{ "--kpi-accent": "#FBBF24" }}>
          <p className="kpi-label">Resources</p>
          <p className="kpi-value">{knowledgeBase.reduce((acc, k) => acc + k.resources.length, 0)}</p>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-wrap items-center gap-4 mb-6" data-testid="knowledge-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B95A5]" size={18} />
          <input
            type="text"
            placeholder={t('modules:knowledgeCenter.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="qms-input pl-10"
            data-testid="knowledge-search"
          />
        </div>
        <div className="filter-pills flex-wrap">
          {domains.map((domain) => (
            <button
              key={domain.id}
              onClick={() => setSelectedDomain(domain.id)}
              className={`filter-pill flex items-center gap-2 ${selectedDomain === domain.id ? "active" : ""}`}
            >
              <domain.icon size={14} />
              <span>{domain.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Knowledge Cards */}
      <div className="space-y-4" data-testid="knowledge-list">
        {filteredKnowledge.map((item) => (
          <div 
            key={item.id}
            className="qms-card overflow-hidden"
            data-testid={`knowledge-${item.id}`}
          >
            {/* Card Header */}
            <div 
              className="p-4 flex items-center gap-4 cursor-pointer hover:bg-[rgba(30,58,95,0.2)] transition-colors"
              onClick={() => toggleTopic(item.id)}
              style={{ borderLeft: `3px solid ${item.color}` }}
            >
              <div 
                className="w-12 h-12 rounded-[3px] flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${item.color}20` }}
              >
                <item.icon size={24} style={{ color: item.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-['Syne'] text-lg font-semibold text-white">{item.title}</h3>
                  <span className="badge badge-blue text-xs capitalize">{item.domain}</span>
                </div>
                <p className="text-[#8B95A5] text-sm">{item.description}</p>
                {/* Abstract Summary - Issue #3 */}
                {item.abstract && (
                  <p className="text-[#CBD5E1] text-xs mt-2 leading-relaxed line-clamp-2">{item.abstract}</p>
                )}
              </div>
              <div className="flex items-center gap-4">
                <div className="hidden md:flex items-center gap-3 text-[#8B95A5] text-xs">
                  <span>{item.topics.length} {t('modules:knowledgeCenter.topicsCount')}</span>
                  <span>{item.tools.length} {t('modules:knowledgeCenter.toolsCount')}</span>
                  <span>{item.resources.length} {t('modules:knowledgeCenter.resourcesCount')}</span>
                </div>
                {expandedTopic === item.id ? (
                  <ChevronDown size={20} className="text-[#8B95A5]" />
                ) : (
                  <ChevronRight size={20} className="text-[#8B95A5]" />
                )}
              </div>
            </div>

            {/* Expanded Content */}
            {expandedTopic === item.id && (
              <div className="border-t border-[rgba(255,255,255,0.07)]">
                {/* Full Abstract */}
                {item.abstract && (
                  <div className="px-4 pt-4 pb-2">
                    <div className="bg-[rgba(54,169,225,0.05)] border border-[rgba(54,169,225,0.12)] rounded-[3px] p-4">
                      <h4 className="text-[#38BDF8] text-xs uppercase tracking-wider mb-2 font-medium">Abstract Summary</h4>
                      <p className="text-[#CBD5E1] text-sm leading-relaxed">{item.abstract}</p>
                    </div>
                  </div>
                )}

                <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[rgba(255,255,255,0.07)]">
                  {/* Topics */}
                  <div className="p-4">
                    <h4 className="font-['Syne'] font-semibold text-white mb-3 flex items-center gap-2">
                      <BookOpen size={16} className="text-[#38BDF8]" />
                      {t('modules:knowledgeCenter.keyTopics')}
                    </h4>
                    <ul className="space-y-2">
                      {item.topics.map((topic, i) => (
                        <li key={i} className="text-[#CBD5E1] text-sm flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#38BDF8] mt-1.5 flex-shrink-0" />
                          {topic}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 pt-4 border-t border-[rgba(255,255,255,0.07)]">
                      <h5 className="text-[#8B95A5] text-xs uppercase mb-2">{t('modules:knowledgeCenter.relatedStandards')}</h5>
                      <div className="flex flex-wrap gap-2">
                        {item.standards.map((std, i) => (
                          <span key={i} className="sector-tag">{std}</span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Tools */}
                  <div className="p-4">
                    <h4 className="font-['Syne'] font-semibold text-white mb-3 flex items-center gap-2">
                      <Calculator size={16} className="text-[#4ADE80]" />
                      {t('modules:knowledgeCenter.toolsAndTemplates')}
                    </h4>
                    <div className="space-y-2">
                      {item.tools.map((tool, i) => (
                        <button
                          key={i}
                          className="w-full flex items-center gap-3 p-2 bg-[#181B23] rounded-[3px] border border-[rgba(255,255,255,0.07)] hover:border-[#38BDF8] transition-colors text-left"
                        >
                          {tool.type === "calculator" ? (
                            <Calculator size={16} className="text-[#FBBF24]" />
                          ) : (
                            <FileText size={16} className="text-[#36A9E1]" />
                          )}
                          <span className="text-[#CBD5E1] text-sm flex-1">{tool.name}</span>
                          <span className="badge badge-gray text-xs">{tool.type}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Resources */}
                  <div className="p-4">
                    <h4 className="font-['Syne'] font-semibold text-white mb-3 flex items-center gap-2">
                      <BookMarked size={16} className="text-[#FBBF24]" />
                      {t('modules:knowledgeCenter.references')}
                    </h4>
                    <div className="space-y-2">
                      {item.resources.map((resource, i) => (
                        <a
                          key={i}
                          href={resource.url}
                          className="flex items-center gap-3 p-2 bg-[#181B23] rounded-[3px] border border-[rgba(255,255,255,0.07)] hover:border-[#38BDF8] transition-colors"
                        >
                          <FileText size={16} className="text-[#38BDF8]" />
                          <span className="text-[#CBD5E1] text-sm flex-1">{resource.title}</span>
                          {resource.type === "whitepaper" && (
                            <span className="text-[10px] text-[#E8A838] bg-[rgba(232,168,56,0.1)] px-1.5 py-0.5 rounded uppercase tracking-wider">Paper</span>
                          )}
                          <ExternalLink size={14} className="text-[#8B95A5]" />
                        </a>
                      ))}
                    </div>

                    {/* AI Button */}
                    <button
                      onClick={() => openAiForTopic(item.title, item.domain)}
                      className="w-full mt-4 flex items-center justify-center gap-2 p-2 bg-[rgba(0,200,224,0.1)] border border-[#38BDF8] rounded-[3px] text-[#38BDF8] hover:bg-[rgba(0,200,224,0.2)] transition-colors text-sm"
                      data-testid={`ask-ai-${item.id}`}
                    >
                      <Sparkles size={16} />
                      Ask AI about {item.title}
                    </button>

                    {/* Client Questionnaire Button - Issue #3 */}
                    <button
                      onClick={() => navigate(`/questionnaire?sector=${encodeURIComponent(item.domain)}&topic=${encodeURIComponent(item.title)}`)}
                      className="w-full mt-2 flex items-center justify-center gap-2 p-2 bg-[rgba(232,168,56,0.1)] border border-[#E8A838] rounded-[3px] text-[#E8A838] hover:bg-[rgba(232,168,56,0.2)] transition-colors text-sm"
                      data-testid={`questionnaire-${item.id}`}
                    >
                      <FileQuestion size={16} />
                      Start Client Questionnaire
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Quick Reference */}
      <div className="mt-8 qms-card" data-testid="quick-reference">
        <div className="qms-card-header flex items-center gap-2">
          <Brain size={18} className="text-[#38BDF8]" />
          Engineering Body of Knowledge Summary
        </div>
        <div className="qms-card-body">
          <div className="grid md:grid-cols-4 gap-6">
            <div>
              <h4 className="font-['Syne'] font-semibold text-white mb-3">Quality Engineering</h4>
              <ul className="text-[#8B95A5] text-sm space-y-1">
                <li>Statistical Process Control (SPC)</li>
                <li>Six Sigma & DMAIC</li>
                <li>Design of Experiments (DOE)</li>
                <li>7 Quality Tools</li>
                <li>Reliability Engineering</li>
                <li>Measurement System Analysis</li>
              </ul>
            </div>
            <div>
              <h4 className="font-['Syne'] font-semibold text-white mb-3">Manufacturing Engineering</h4>
              <ul className="text-[#8B95A5] text-sm space-y-1">
                <li>Lean Manufacturing</li>
                <li>Process Engineering</li>
                <li>Design for Manufacturing</li>
                <li>Process Validation</li>
                <li>Equipment Qualification</li>
              </ul>
            </div>
            <div>
              <h4 className="font-['Syne'] font-semibold text-white mb-3">Regulatory & Risk</h4>
              <ul className="text-[#8B95A5] text-sm space-y-1">
                <li>GMP / FDA Compliance</li>
                <li>ISO Standards</li>
                <li>FMEA / Risk Analysis</li>
                <li>Supplier Quality</li>
                <li>Metrology & Calibration</li>
              </ul>
            </div>
            <div>
              <h4 className="font-['Syne'] font-semibold text-white mb-3">AI & Emerging Tech</h4>
              <ul className="text-[#8B95A5] text-sm space-y-1">
                <li>ISO/IEC 42001 (AI Management)</li>
                <li>EU AI Act Compliance</li>
                <li>AI in Medical Devices (SaMD)</li>
                <li>AI in Automotive (ADAS/AV)</li>
                <li>NIST AI Risk Management</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      {/* AI Chat Panel */}
      {aiPanelOpen && (
        <div className="fixed right-0 top-0 h-screen w-[400px] bg-[#181B23] border-l border-[rgba(255,255,255,0.07)] z-50 flex flex-col shadow-2xl" data-testid="ai-chat-panel">
          {/* Panel Header */}
          <div className="flex items-center justify-between p-4 border-b border-[rgba(255,255,255,0.07)]">
            <div className="flex items-center gap-2">
              <Sparkles size={18} className="text-[#38BDF8]" />
              <span className="font-['Syne'] font-semibold text-white">CHARSTAN AI</span>
              {aiContext && <span className="badge badge-cyan text-xs capitalize">{aiContext}</span>}
            </div>
            <button
              onClick={() => setAiPanelOpen(false)}
              className="text-[#8B95A5] hover:text-white transition-colors"
              data-testid="close-ai-panel"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {aiMessages.length === 0 && (
              <div className="text-center py-12 text-[#8B95A5]">
                <MessageSquare size={36} className="mx-auto mb-3 opacity-40" />
                <p className="text-sm">Ask me anything about quality & manufacturing engineering</p>
              </div>
            )}
            {aiMessages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3 py-2.5 rounded-[3px] text-sm leading-relaxed ${
                    msg.role === "user"
                      ? "bg-[#36A9E1] text-white"
                      : "bg-[#1F222D] text-[#CBD5E1] border border-[rgba(255,255,255,0.07)]"
                  }`}
                >
                  <div className="whitespace-pre-wrap">{msg.text}</div>
                </div>
              </div>
            ))}
            {aiLoading && (
              <div className="flex justify-start">
                <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] px-4 py-3 rounded-[3px] flex items-center gap-2 text-[#8B95A5] text-sm">
                  <Loader2 size={14} className="animate-spin" />
                  Thinking...
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-[rgba(255,255,255,0.07)]">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                sendAiMessage(aiInput);
              }}
              className="flex gap-2"
            >
              <input
                type="text"
                value={aiInput}
                onChange={(e) => setAiInput(e.target.value)}
                placeholder="Ask about standards, processes..."
                className="qms-input flex-1"
                disabled={aiLoading}
                data-testid="ai-chat-input"
              />
              <button
                type="submit"
                disabled={aiLoading || !aiInput.trim()}
                className="btn-primary px-3 py-2 disabled:opacity-40"
                data-testid="ai-chat-send"
              >
                <Send size={16} />
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Floating AI Button */}
      {!aiPanelOpen && (
        <button
          onClick={() => setAiPanelOpen(true)}
          className="fixed bottom-6 right-6 w-12 h-12 bg-[#36A9E1] hover:bg-[#38BDF8] text-white rounded-full flex items-center justify-center shadow-lg transition-all z-50 hover:scale-105"
          data-testid="ai-fab"
        >
          <Sparkles size={20} />
        </button>
      )}
    </QMSLayout>
  );
};

export default KnowledgeCenterPage;
